'use client';

/**
 * DreamTwinSyncBridge — Automatically syncs DreamMemory + BusinessProfile into DreamTwin.
 * Also runs proactive recommendation engine.
 * Drop this inside any layout that has access to all three contexts.
 * It runs silently in the background — no UI.
 */

import { useEffect, useRef } from 'react';
import { useDreamMemory } from '@/lib/dreamMemory';
import { useBusinessProfile } from '@/lib/businessProfile';
import { useDreamTwin } from '@/lib/dreamTwin';
import { useProactiveRecommendations } from '@/lib/hooks/useProactiveRecommendations';

export default function DreamTwinSyncBridge() {
  const { memory } = useDreamMemory();
  const { profile } = useBusinessProfile();
  const { syncFromMemory, syncFromProfile, initTwin } = useDreamTwin();

  const lastMemoryRef = useRef<string>('');
  const lastProfileRef = useRef<string>('');

  // Run proactive recommendations engine
  useProactiveRecommendations();

  // Init twin when memory has a projectId
  useEffect(() => {
    if (memory?.projectId) {
      initTwin(memory?.projectId);
    }
  }, [memory?.projectId, initTwin]);

  // Sync from DreamMemory whenever it changes
  useEffect(() => {
    if (!memory) return;
    const key = JSON.stringify({
      identity: memory?.identity,
      brand: memory?.brand,
      audience: memory?.audience,
      products: memory?.products,
      strategy: memory?.strategy,
      project: memory?.project,
      generatedVisuals: memory?.generatedVisuals?.length,
    });
    if (key !== lastMemoryRef?.current) {
      lastMemoryRef.current = key;
      syncFromMemory(memory);
    }
  }, [memory, syncFromMemory]);

  // Sync from BusinessProfile whenever it changes
  useEffect(() => {
    if (!profile) return;
    const key = JSON.stringify({
      business_type: profile?.business_type,
      industry: profile?.industry,
      niche: profile?.niche,
      tone: profile?.tone,
      brand_personality: profile?.brand_personality,
    });
    if (key !== lastProfileRef?.current) {
      lastProfileRef.current = key;
      syncFromProfile(profile);
    }
  }, [profile, syncFromProfile]);

  return null;
}
